<template>
  <div class="page-wrap">

    <!-- ── Page Header ── -->
    <div class="page-header">
      <div class="page-header__left">
        <button class="btn-back" @click="$router.back()">
          <i class="fa fa-arrow-left"></i>
        </button>
        <div>
          <h1 class="page-title">{{ city ? city.name : 'Edit City' }}</h1>
          <span class="page-subtitle">City #{{ id }}</span>
        </div>
      </div>
    </div>

    <!-- ── Photo ── -->
    <div class="panel">
      <div class="panel-title"><i class="fa fa-photo"></i> Photo</div>

      <div class="form-field" style="max-width: 420px;">
        <label class="filter-label" for="cityPhoto">Choose a photo...</label>
        <input
            id="cityPhoto"
            type="file"
            accept="image/*"
            class="dd-input dd-input--file"
            ref="uploader"
            @change="upload"
        />
      </div>

      <div class="progress mt-3" v-if="fileSelected">
        <div
            :class="'progress-bar progress-bar-striped ' + progressClass"
            role="progressbar"
            :style="'width: ' + progressValue + '%'"
            :aria-valuenow="progressValue"
            aria-valuemin="0"
            aria-valuemax="100"
        ></div>
      </div>

      <div class="photo-preview" v-if="previewUrl">
        <img :src="previewUrl" alt="City photo" />
      </div>
      <div class="photo-empty" v-else>
        <i class="fa fa-picture-o"></i> No photo yet
      </div>
    </div>

    <!-- ── Errors ── -->
    <ul class="error-list" v-if="errors.length">
      <li v-for="error in errors" :key="error">{{ error }}</li>
    </ul>

    <!-- ── Submit ── -->
    <div class="form-actions">
      <button class="btn-cancel" @click="$router.back()">
        <i class="fa fa-times"></i> Cancel
      </button>
      <button class="btn-save" @click="submit()" :disabled="!form.photo">
        <i class="fa fa-check"></i> Save Photo
      </button>
    </div>

  </div>
</template>

<script>
import { config } from "../../helpers";

export default {
  name: "CityEdit",
  data() {
    return {
      id: this.$route.params.id,
      url: "",
      city: null,
      previewUrl: null,
      form: { photo: null },

      fileSelected: false,
      progressClass: "",
      progressValue: 20,

      errors: [],
    };
  },

  mounted() {
    this.url = config("BACKEND_ROOT");
    this.loadCity();
  },

  methods: {
    // `/cities` is a search endpoint with no show route, so look the city up by
    // the name carried over from the list (kept in the query so a refresh works).
    loadCity() {
      axios
          .get(`${this.url}/cities`, { params: { q: this.$route.query.name || undefined } })
          .then((r) => {
            this.city = r.data.data.find((c) => String(c.id) === String(this.id)) ?? null;
            this.previewUrl = this.city && this.city.photo ? this.city.photo.url : null;
          });
    },

    upload() {
      this.selectedFile = this.$refs.uploader.files[0];
      if (!this.selectedFile) return;

      const type = this.selectedFile.type.split("/");

      this.fileSelected = true;
      this.progressClass = "";
      this.progressValue = 20;
      this.errors = [];

      const formData = new FormData();
      formData.append("file", this.selectedFile);
      formData.append("type", type[0]);

      const headers = { "Content-Type": "multipart/form-data" };

      axios
          .post(`${this.url}/admin/uploads`, formData, { headers })
          .then((res) => {
            this.form.photo = res.data.data.file_name;
            this.previewUrl = res.data.data.url;

            this.progressClass = "bg-success";
            this.progressValue = 100;
          })
          .catch((error) => {
            this.progressClass = "bg-danger";
            this.progressValue = 100;

            alert(error.response.data.errors.file[0]);
          });
    },

    submit() {
      axios
          .put(`${this.url}/admin/cities/${this.id}`, { photo: this.form.photo })
          .then(() => this.$router.push("/cities"))
          .catch((e) => { this.errors = Object.values(e.response.data.errors).flat(); });
    },
  },
};
</script>

<style scoped>
.page-wrap { display: flex; flex-direction: column; gap: 16px; }

/* ── Header ── */
.page-header { display: flex; align-items: flex-start; gap: 12px; }
.page-header__left { display: flex; align-items: flex-start; gap: 12px; }
.btn-back { width: 34px; height: 34px; margin-top: 4px; background: var(--bg-hover); border: 1px solid var(--border); border-radius: 8px; color: var(--text-2); cursor: pointer; display: flex; align-items: center; justify-content: center; flex-shrink: 0; transition: border-color .15s, color .15s; }
.btn-back:hover { border-color: var(--accent); color: var(--accent); }
.page-title    { font-size: 20px; font-weight: 500; color: var(--text-1); margin: 0 0 2px; }
.page-subtitle { font-size: 12px; color: var(--text-3); }

/* ── Panel ── */
.panel { background: var(--bg-panel); border: 1px solid var(--border); border-radius: 12px; padding: 1.25rem; }
.panel-title { font-size: 12px; font-weight: 600; color: var(--text-3); text-transform: uppercase; letter-spacing: .5px; margin-bottom: 1.1rem; display: flex; align-items: center; gap: 7px; }

/* ── Form ── */
.form-field { display: flex; flex-direction: column; gap: 5px; }
.mt-3 { margin-top: 1rem; }
.filter-label { font-size: 11px; font-weight: 500; color: var(--text-3); text-transform: uppercase; letter-spacing: .4px; }

.dd-input { height: 36px; background: var(--bg-base); border: 1px solid var(--border); border-radius: 8px; color: var(--text-1); font-size: 13px; padding: 0 10px; outline: none; width: 100%; transition: border-color .15s; }
.dd-input:focus { border-color: var(--accent); }
.dd-input--file { padding: 5px 10px; cursor: pointer; }

/* ── Preview ── */
.photo-preview { margin-top: 1rem; width: 260px; border-radius: 10px; overflow: hidden; border: 1px solid var(--border); }
.photo-preview img { width: 100%; display: block; }
.photo-empty { margin-top: 1rem; display: flex; align-items: center; gap: 7px; font-size: 12px; color: var(--text-3); }

/* ── Errors ── */
.error-list { padding-left: 16px; color: var(--accent); font-size: 13px; }

/* ── Actions ── */
.form-actions { display: flex; justify-content: flex-end; align-items: center; gap: 10px; }
.btn-cancel { height: 38px; padding: 0 18px; background: var(--bg-hover); border: 1px solid var(--border); border-radius: 9px; color: var(--text-2); font-size: 13px; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: border-color .15s; }
.btn-cancel:hover { border-color: var(--border-2); }
.btn-save { height: 38px; padding: 0 22px; background: var(--accent); border: none; border-radius: 9px; color: #fff; font-size: 13px; font-weight: 500; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: opacity .15s; }
.btn-save:hover:not(:disabled) { opacity: .88; }
.btn-save:disabled { opacity: .45; cursor: not-allowed; }
</style>
